import React, { useState } from 'react';
import { timelineData } from '../../data/timelineData';

const WorkTimeline = () => {
  const [expandedItems, setExpandedItems] = useState({});

  const toggleTimelineContent = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <section id="experience" className="bg-apple-gray-100 pb-16">
      <div className="pt-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          {/* Timeline Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-apple-gray-800 mb-4">
              Professional Journey
            </h2>
            <h3 className="text-xl text-apple-gray-600 mb-6 font-medium">
              From Software Engineering to Product Leadership
            </h3>
            <div className="max-w-3xl mx-auto">
              <p className="text-lg text-apple-gray-700 leading-relaxed">
                My career has been defined by transforming chaos into order and turning struggling teams into high-performing engines of growth.
                From saving a startup with €20K left to leading 40+ developers across B2B products, I consistently deliver measurable impact
                through strategic product management and process optimization.
              </p>
            </div>
          </div>

          {/* Modern Timeline */}
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 top-0 h-full w-0.5 bg-apple-gray-400"></div>

            {timelineData.map((item, index) => (
              <div key={item.id} className="relative mb-12">
                {/* Timeline dot */}
                <div className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 -top-1 w-6 h-6 rounded-full bg-apple-gray-700 shadow-md z-10 flex items-center justify-center">
                  <div className="w-3 h-3 rounded-full bg-white"></div>
                </div>

                {/* Content container - alternating layout */}
                <div className="mx-auto md:ml-0 relative md:grid md:grid-cols-2 md:gap-8 items-start">
                  {index % 2 === 0 ? (
                    // Even items (0, 2, 4): Date left, Content right
                    <>
                      {/* Date on left */}
                      <div className="hidden md:block md:text-right pr-8">
                        <span className="bg-apple-gray-700 text-apple-gray-100 font-medium px-3 py-1.5 rounded-full text-sm inline-block shadow-sm">
                          {item.date}
                        </span>
                      </div>

                      {/* Content on right */}
                      <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-apple-gray-200 hover:shadow-md transition-shadow duration-300">
                        {/* Mobile date */}
                        <div className="md:hidden mb-3">
                          <span className="bg-apple-gray-700 text-apple-gray-100 font-medium px-3 py-1.5 rounded-full text-sm inline-block shadow-sm">
                            {item.date}
                          </span>
                        </div>

                        <h3 className="text-xl font-semibold text-apple-gray-800 mb-2">{item.company}</h3>
                        <p className="font-medium text-apple-gray-700 mb-3">{item.position}</p>

                        <div className="text-apple-gray-600 leading-relaxed space-y-3">
                          {/* Always visible truncated summary */}
                          <p>
                            {expandedItems[index]
                              ? item.content.summary
                              : `${item.content.summary.substring(0, 120)}${item.content.summary.length > 120 ? '...' : ''}`
                            }
                          </p>

                          {/* Expandable additional content */}
                          {item.content.details && (
                            <div className={`transition-all duration-300 overflow-hidden ${expandedItems[index] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                              }`}>
                              {Array.isArray(item.content.details) ? (
                                <ul className="list-disc pl-5 space-y-2">
                                  {item.content.details.map((detail, detailIndex) => (
                                    <li key={detailIndex}>
                                      {detail.includes(':') ? (
                                        <>
                                          <span className="font-medium">{detail.split(':')[0]}:</span>
                                          {detail.split(':').slice(1).join(':')}
                                        </>
                                      ) : (
                                        detail
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p>{item.content.details}</p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Toggle button */}
                        {(item.content.summary.length > 120 || item.content.details) && (
                          <div
                            className="mt-4 cursor-pointer text-apple-gray-600 hover:text-apple-gray-700 transition-colors duration-200"
                            onClick={() => toggleTimelineContent(index)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-6 w-6 transition-transform duration-300 ${expandedItems[index] ? 'rotate-180' : ''
                                }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    // Odd items (1, 3): Content left, Date right
                    <>
                      {/* Content on left */}
                      <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-apple-gray-200 hover:shadow-md transition-shadow duration-300">
                        {/* Mobile date */}
                        <div className="md:hidden mb-3">
                          <span className="bg-apple-gray-700 text-apple-gray-100 font-medium px-3 py-1.5 rounded-full text-sm inline-block shadow-sm">
                            {item.date}
                          </span>
                        </div>

                        <h3 className="text-xl font-semibold text-apple-gray-800 mb-2">{item.company}</h3>
                        <p className="font-medium text-apple-gray-700 mb-3">{item.position}</p>

                        <div className="text-apple-gray-600 leading-relaxed space-y-3">
                          {/* Always visible truncated summary */}
                          <p>
                            {expandedItems[index]
                              ? item.content.summary
                              : `${item.content.summary.substring(0, 120)}${item.content.summary.length > 120 ? '...' : ''}`
                            }
                          </p>

                          {/* Expandable additional content */}
                          {item.content.details && (
                            <div className={`transition-all duration-300 overflow-hidden ${expandedItems[index] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                              }`}>
                              {Array.isArray(item.content.details) ? (
                                <ul className="list-disc pl-5 space-y-2">
                                  {item.content.details.map((detail, detailIndex) => (
                                    <li key={detailIndex}>
                                      {detail.includes(':') ? (
                                        <>
                                          <span className="font-medium">{detail.split(':')[0]}:</span>
                                          {detail.split(':').slice(1).join(':')}
                                        </>
                                      ) : (
                                        detail
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p>{item.content.details}</p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Toggle button */}
                        {(item.content.summary.length > 120 || item.content.details) && (
                          <div
                            className="mt-4 cursor-pointer text-apple-gray-600 hover:text-apple-gray-700 transition-colors duration-200"
                            onClick={() => toggleTimelineContent(index)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-6 w-6 transition-transform duration-300 ${expandedItems[index] ? 'rotate-180' : ''
                                }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Date on right */}
                      <div className="hidden md:block md:pl-8">
                        <span className="bg-apple-gray-700 text-apple-gray-100 font-medium px-3 py-1.5 rounded-full text-sm inline-block shadow-sm">
                          {item.date}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}

            {/* Timeline end dot */}
            <div className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 bottom-0 w-6 h-6 rounded-full bg-apple-gray-700 shadow-md z-10 flex items-center justify-center">
              <div className="w-3 h-3 rounded-full bg-white"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkTimeline;